import React from "react";

import { css, useTheme } from "@emotion/react";
import classNames from "classnames";

import useComponentVisible from "../../../../hooks/useComponentVisible";
import {
    useHoyerAclStore,
    VacancyPermission,
    PermissionAdminPermission,
    CustomerAccessPermission,
    UserPermission,
    TenderPermission,
} from "../../../../hooks/useHoyerAcl";
import {
    useIsAuthenticated,
    useIsBetaTester,
    useKeycloakOpt,
} from "../../../../providers/AuthProvider";
import PredefinedIcon, {
    PredefinedIconType,
} from "../../icons/Icon/Predefined";
import LinkComponent from "../../LinkComponent";
import SubNav from "../Desktop/LinkItems/SubNav";
import {
    LinkItemClickable,
    StyledIconContainer,
    SubNavContainer,
} from "../Desktop/TopRow/TopLinkItems";
import useNavigationStore from "../store";

/**
 * Returns an array to create the account navigation.
 */
export const GetAccountSection = () => {
    const { can } = useHoyerAclStore();
    const isAuthenticated = useIsAuthenticated();
    const isBetaTester = useIsBetaTester();

    if (!isAuthenticated) return;

    const hoyerCardAccountLinks = [
        { href: "/hoyercard-portal", text: "Übersicht", permission: null },
        {
            href: "/hoyercard-portal/activation",
            text: <>Kundennummer&nbsp;aktivieren</>,
            permission: CustomerAccessPermission.ACTIVATE_CUSTOMER,
        },
        {
            href: "/hoyercard-portal/admin",
            text: "Admin-Bereich",
            anyPermissions: [
                UserPermission.CONNECT_TO_CUSTOMER,
                UserPermission.GENERATE_ACTIVATION_CODE,
                UserPermission.DISCONNECT_FROM_CUSTOMER,
            ],
        },
    ];

    const hoyerCardAccountV2Links = [
        { href: "/hoyercard-portal/v2", text: "Übersicht", permission: null },
        {
            href: "/hoyercard-portal/v2/activation",
            text: <>Geschäftspartnernummer&nbsp;aktivieren</>,
            permission: CustomerAccessPermission.ACTIVATE_CUSTOMER,
        },
        {
            href: "/hoyercard-portal/v2/admin",
            text: "Admin-Bereich",
            anyPermissions: [
                UserPermission.CONNECT_TO_CUSTOMER,
                UserPermission.GENERATE_ACTIVATION_CODE,
                UserPermission.DISCONNECT_FROM_CUSTOMER,
            ],
        },
    ];

    const callForTenderAccountLinks = [
        { href: "/ausschreibungsportal", text: "Übersicht" },
        {
            href: "/ausschreibungsportal/profil",
            text: "Ausschreibungsprofil",
        },
        {
            href: "/ausschreibungsportal/admin",
            text: "Admin-Bereich",
            permission: TenderPermission.MANAGE,
        },
    ];

    const vacancyAccountLinks = [
        {
            href: "/stellenangebote/admin",
            text: "Admin-Bereich",
            permission: VacancyPermission.MANAGE,
        },
    ];

    const isAllowed = (link) => {
        if (!link.permission && !link.anyPermissions) {
            return true;
        }
        if (link.permission) {
            return can(link.permission);
        }
        if (link.anyPermissions) {
            return link.anyPermissions.some((perm) => can(perm));
        }
        return false;
    };

    return {
        name: "Mein Konto",
        subsections: [
            ...(isBetaTester
                ? [
                      {
                          name: "Hoyer Card Portal V2",
                          items: hoyerCardAccountV2Links.filter(isAllowed),
                      },
                  ]
                : []),
            {
                name: "Hoyer Card Portal",
                items: hoyerCardAccountLinks.filter(isAllowed),
            },
            {
                name: "Ausschreibungsportal",
                items: callForTenderAccountLinks.filter(isAllowed),
            },
            ...(can(VacancyPermission.MANAGE)
                ? [
                      {
                          name: "Stellenangebote",
                          items: vacancyAccountLinks.filter(isAllowed),
                      },
                  ]
                : []),
        ],
    };
};

const HoyerCard = () => {
    const { ref, isVisible, setIsVisible } = useComponentVisible(false);
    const isAuthenticated = useIsAuthenticated();

    const accountSection = GetAccountSection();
    const [path] = useNavigationStore((state) => [state.path]);
    if (!isAuthenticated) return null;

    return (
        <SubNavContainer
            ref={ref}
            className={classNames({
                active: isVisible,
            })}
            onClick={() => setIsVisible(!isVisible)}
        >
            <LinkItemClickable
                active={
                    path &&
                    (path.includes("hoyercard-portal") ||
                        path.includes("ausschreibungsportal") ||
                        path.includes("stellenangebote/admin"))
                }
            >
                <StyledIconContainer>
                    <PredefinedIcon
                        type={PredefinedIconType.user}
                        color="#05235f"
                    />
                </StyledIconContainer>
                <span>{accountSection.name}</span>
            </LinkItemClickable>
            <SubNav subsections={accountSection.subsections} isOpen={isVisible}>
                <LogoutSection />
            </SubNav>
        </SubNavContainer>
    );
};

export const LogoutSection = () => {
    const { can } = useHoyerAclStore();
    const { authenticated, email } = useKeycloakOpt();
    const theme = useTheme();

    const containerStyles = css`
        display: flex;
        flex-wrap: wrap;
        gap: 30px;
        align-items: center;
        justify-content: space-between;
        padding-top: 20px;
        margin-top: 20px;
        color: ${theme.colors.blue.toString()};
        border-top: 1px solid ${theme.colors.grey.toString()};
    `;

    if (!authenticated) {
        return null;
    }

    return (
        <div css={containerStyles}>
            <span>{email}</span>
            {can(PermissionAdminPermission.MANAGE) && (
                <LinkComponent
                    link={{
                        text: "Rollen und Rechte",
                        href: "/permission-admin",
                    }}
                />
            )}
            <LinkComponent button link={{ text: "Logout", href: "/logout" }} />
        </div>
    );
};

export default HoyerCard;
